import {
  Component,
  ElementRef,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { environment } from "@env/environment";
import { GameSettingsService } from "../../../../game-settings.service";
import { TweenMax, Power1 } from "gsap";

declare var confetti: any;

@Component({
  selector: "positive-reward-preview",
  templateUrl: "./positive-reward-preview.component.html",
  styleUrls: ["./positive-reward-preview.component.scss"]
})
export class PositiveRewardPreviewComponent implements OnInit, OnChanges, OnDestroy {

  @Input() coinIcon: string = "";
  
  // Support both 'thumbnail' and 'imageArt' inputs
  @Input() thumbnail: string = "";
  @Input('imageArt') set imageArt(val: string) { this.thumbnail = val; }

  @Input() backgroundImage: string = "";

  // Support both 'backgroundColor1' and 'color1' inputs
  @Input() backgroundColor1: string = "";
  @Input('color1') set color1(val: string) { this.backgroundColor1 = val; }

  // Support both 'backgroundColor2' and 'color2' inputs
  @Input() backgroundColor2: string = "";
  @Input('color2') set color2(val: string) { this.backgroundColor2 = val; }

  @Input() counterColor1: string = "";
  @Input() counterColor2: string = "";
  @Input() rewardAmount: string | number = 0;
  @Input() thumbnailScale: number = 1;
  @Input() backgroundScale: number = 1;
  @Input() isExpand: boolean = false;

  @ViewChild("myCounter") myCounter: ElementRef;

  counter: string = "0";
  private endValue: number = 0;
  isCounterEnded: boolean = false;

  private confettiTimeout: any = null;
  private confettiScriptLoaded: boolean = false;

  s3BucketUrl: string = environment.s3BucketUrl;

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private gameSettingsService: GameSettingsService
  ) {}

  ngOnInit() {
    this.updateEndValue();
    this.animateCount();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["counterColor1"] || changes["counterColor2"] || changes["rewardAmount"]) {
      this.updateEndValue();
      this.animateCount();
    }
  }

  private updateEndValue() {
    // Prioritize input rewardAmount if > 0, fallback to service default otherwise
    const val = this.safeNumber(this.rewardAmount);
    if (val > 0) {
      this.endValue = val;
    } else {
      this.endValue = this.safeNumber(this.gameSettingsService.rewardAmount);
    }
  }

  ngOnDestroy() {
    if (this.confettiTimeout) clearTimeout(this.confettiTimeout);
  }

  private safeNumber(value: any): number {
    const n = parseInt(value);
    return isNaN(n) ? 0 : n;
  }

  animateCount() {
    if (!this.myCounter || !this.myCounter.nativeElement) return;

    const el = this.myCounter.nativeElement;
    this.counter = "0";
    this.isCounterEnded = false;

    const duration = 900;
    const startTime = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const value = Math.floor(progress * this.endValue);
      this.counter = value.toLocaleString();

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        this.finishCount();
      }
    };

    requestAnimationFrame(tick);
  }

  private finishCount() {
    this.counter = this.endValue.toLocaleString();
    this.isCounterEnded = true;
    this.scaleUpCount();
    this.animateGradient();
    this.celebrate();
  }

  private scaleUpCount() {
    if (!this.myCounter || !this.myCounter.nativeElement) return;
    const el = this.myCounter.nativeElement;

    // Scale up
    TweenMax.fromTo(el, 0.8, { scale: 1 }, { scale: 1.2, ease: Power1.easeOut });
    // Scale back
    TweenMax.to(el, 0.5, { scale: 1, delay: 0.8 });
  }

  private animateGradient() {
    if (!this.myCounter || !this.myCounter.nativeElement) return;

    const gradientStop1 = this.counterColor1 || "#ff5acd";
    const gradientStop2 = this.counterColor2 || "#fbda61";

    const el = this.myCounter.nativeElement;
    this.renderer.setStyle(el, "background-image", `linear-gradient(45deg, ${gradientStop1}, ${gradientStop2}, ${gradientStop1})`);
    this.renderer.setStyle(el, "background-size", "200% auto");
    this.renderer.setStyle(el, "-webkit-background-clip", "text");
    this.renderer.setStyle(el, "background-clip", "text");
    this.renderer.setStyle(el, "-webkit-text-fill-color", "transparent");
    this.renderer.setStyle(el, "color", "transparent");

    TweenMax.to(el, 4, { backgroundPosition: "200% center", repeat: -1, yoyo: true, ease: Power1.easeNone });
  }

  celebrate() {
    const container = this.elementRef.nativeElement.querySelector(".confetti-container");
    if (!container) return;

    if (!this.confettiScriptLoaded) {
      this.loadScript(() => {
        this.confettiScriptLoaded = true;
        this.createConfetti(container);
      });
    } else {
      this.createConfetti(container);
    }
  }

  private loadScript(callback: () => void) {
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/canvas-confetti@1.5.1/dist/confetti.browser.min.js";
    script.onload = callback;
    document.body.appendChild(script);
  }

  private createConfetti(container: HTMLElement) {
    if (this.confettiTimeout) clearTimeout(this.confettiTimeout);

    const canvas = document.createElement("canvas");
    canvas.style.position = "absolute";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.top = "0";
    canvas.style.left = "0";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = "999999";
    container.appendChild(canvas);

    const confettiInstance = confetti.create(canvas, { resize: true });
    confettiInstance({ particleCount: 200, scalar: 0.5, origin: { y: 1 } });

    this.confettiTimeout = setTimeout(() => {
      if (canvas.parentNode === container) container.removeChild(canvas);
      this.confettiTimeout = null;
    }, 20000);
  }

  resolveSrc(value: string): string {
    if (!value) return "/assets/images/image-not-found-150x150.png";
    const v = value.trim();
    if (/^(data:|https?:|blob:)/i.test(v)) return v;
    if (v.startsWith("/assets/")) return v;
    return this.s3BucketUrl + v;
  }

  getBackgroundImage(): string {
    const gradient =
      this.backgroundColor1 && this.backgroundColor2
        ? `linear-gradient(-225deg, ${this.backgroundColor1}, ${this.backgroundColor2})`  
        : "";
    const image = this.backgroundImage ? `url('${this.resolveSrc(this.backgroundImage)}')` : "";

    if (gradient && image) return `${image}, ${gradient}`;
    if (image) return image;
    if (gradient) return gradient;
    return "";
  }

  getBackgroundSize(): string {
    const s = this.backgroundScale && this.backgroundScale > 0 ? this.backgroundScale : 1;
    // If both image and gradient exist, specify two background-size values
    if (this.backgroundImage && this.backgroundColor1 && this.backgroundColor2) {
      return `${Math.round(s * 100)}% auto, cover`;
    }
    // If only image exists, scale it; otherwise default cover
    if (this.backgroundImage) {
      return `${Math.round(s * 100)}% auto`;
    }
    return "cover";
  }
}
